import type { IPitcherClient } from "@codesandbox/pitcher-client";
import { GlobalError } from "components/Error/GlobalError";
import { useEnvironmentInterface } from "environment-interface";
import {
  LegacyPitcherProvider,
  PitcherProvider,
} from "features/pitcher/Feature";
import { useEffect, useState } from "react";

import { BlockingLoader } from "components/BlockingLoader";

import { EditorLayout } from "./EditorLayout";

export const InitBrowserPitcher: React.FC<{
  id: string;
  seamlessFork: boolean;
  children: React.ReactNode;
}> = ({ id, seamlessFork, children }) => {
  const environment = useEnvironmentInterface();
  const [pitcherState, setPitcherState] = useState<
    | {
        state: "INITIALIZING";
      }
    | {
        state: "READY";
        client: IPitcherClient;
      }
    | {
        state: "ERROR";
        error: unknown;
      }
  >({
    state: "INITIALIZING",
  });

  // Expose CSB api requests
  // @ts-ignore
  // eslint-disable-next-line
  const csbApi = environment.csbApi!;

  useEffect(() => {
    let isMounted = true;

    environment.pitcherInstanceManager
      .getBrowserInstance(id, seamlessFork)
      .then((client) => {
        if (isMounted === false) {
          // if at this point isMounted is false, the component has been unmounted
          // so we need to dispose the client to avoid memory leaks and doubled clients
          client.dispose();

          return;
        }
        setPitcherState({ state: "READY", client });
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        setPitcherState({ state: "ERROR", error });
      });

    return () => {
      isMounted = false;

      if (pitcherState.state === "READY") {
        pitcherState.client.dispose();
      }
    };
  }, []);

  useEffect(() => {
    window.onbeforeunload = (event) => {
      if (pitcherState.state !== "READY") {
        return;
      }

      if (
        pitcherState.state === "READY" &&
        pitcherState.client.clients.file.hasDirtyFiles() &&
        !window.confirm(
          "You have unsaved changes in your editor, are you sure you want to navigate away?",
        )
      ) {
        event.preventDefault();
      }
    };
  }, [pitcherState]);

  switch (pitcherState.state) {
    case "INITIALIZING": {
      return (
        <EditorLayout>
          <BlockingLoader
            loadingBarEnd={75}
            loadingBarProgress={20}
            loadingBarSpeed={6}
            loadingBarStart={20}
          >
            Loading...
          </BlockingLoader>
        </EditorLayout>
      );
    }
    case "ERROR": {
      return (
        <EditorLayout>
          <GlobalError
            context={{
              error: String(pitcherState.error),
              branchId: id,
              state: "ERROR",
              event: "Not able to load sandbox browser instance",
              originalError: pitcherState.error,
            }}
            errorCode="SANDBOX_NOT_STARTED"
          />
        </EditorLayout>
      );
    }
    case "READY": {
      return (
        <PitcherProvider client={pitcherState.client} id={id}>
          <LegacyPitcherProvider client={pitcherState.client}>
            {children}
          </LegacyPitcherProvider>
        </PitcherProvider>
      );
    }
  }
};
